<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { ArrowRight, ArrowDown } from 'lucide-vue-next'

import BaseIcon from '@/components/BaseIcon.vue'
import CodeWindow from '@/components/CodeWindow.vue'
import { useResume } from '@/composables/useResume'
import { useSceneParallax } from '@/composables/useSceneParallax'

const { content } = useResume()

const hero = computed(() => content.value.hero)
const initials = computed(() =>
  hero.value.name
    .split(' ')
    .map((word) => word.charAt(0))
    .slice(0, 2)
    .join(''),
)

// Ротация активной строки роли каждые 2200 мс.
const activeRole = ref(0)
let rotation = 0
onMounted(() => {
  rotation = window.setInterval(() => {
    activeRole.value = (activeRole.value + 1) % hero.value.roleLines.length
  }, 2200)
})
onUnmounted(() => clearInterval(rotation))

const scene = ref<HTMLElement | null>(null)
const sceneInner = ref<HTMLElement | null>(null)
useSceneParallax(scene, sceneInner)
</script>

<template>
  <section id="about" class="hero">
    <div class="wrap hero-grid">
      <div class="hero-copy">
        <div class="hero-kicker reveal"><span class="pulse"></span>{{ hero.kicker }}</div>
        <h1 class="hero-name reveal d1"><span class="grad-text">{{ hero.name }}</span></h1>
        <div class="hero-roles reveal d2">
          <span
            v-for="(line, i) in hero.roleLines"
            :key="i"
            class="line"
            :class="{ on: i === activeRole }"
          >{{ line }}</span>
        </div>
        <p class="hero-summary reveal d3">{{ hero.summary }}</p>
        <div class="hero-actions reveal d4">
          <a class="btn btn-primary" href="#contact">{{ hero.cta }}<BaseIcon :icon="ArrowRight" /></a>
          <a class="btn btn-ghost" href="#experience">{{ hero.cta2 }}<BaseIcon :icon="ArrowDown" /></a>
        </div>
        <div class="hero-status reveal d5">
          <span class="seg live"><b>{{ hero.status.a }}</b></span>
          <span class="seg"><b>{{ hero.status.b }}</b></span>
          <span class="seg"><b>{{ hero.status.c }}</b></span>
          <span class="seg"><b>{{ hero.status.d }}</b></span>
        </div>
      </div>

      <div ref="scene" class="scene reveal d2">
        <div ref="sceneInner" class="scene-inner">
          <CodeWindow />

          <div class="float-card fc-avatar2 fc-a" data-depth="54">
            <span class="ring"><span class="inner">{{ initials }}</span></span>
            <span>
              <div class="nm">{{ hero.name }}</div>
              <div class="rl">Frontend · Team Lead</div>
            </span>
          </div>
          <div class="float-card fc-mini fc-b" data-depth="46">
            <div class="fc-chip"><span class="d live"></span><span class="lbl">build</span><span class="val">passing</span></div>
          </div>
          <div class="float-card fc-mini fc-c" data-depth="42">
            <div class="fc-chip"><span class="d"></span><span class="lbl">leading</span><span class="val">10 eng</span></div>
          </div>
          <div class="float-card fc-mini fc-d" data-depth="50">
            <div class="fc-chip"><span class="d"></span><span class="lbl">uptime</span><span class="val">99.9%</span></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
